import { useState } from "react"
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"
import { supabase } from "@/integrations/supabase/client"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { useToast } from "@/hooks/use-toast"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { Database } from "@/integrations/supabase/types"

type OpportunityFormat = Database["public"]["Enums"]["opportunity_format"]
type RateCard = Database["public"]["Tables"]["host_rate_card"]["Row"]

export function RateCardManager({ profile, session }: { profile: any | null, session: any | null }) {
  const { toast } = useToast()
  const queryClient = useQueryClient()
  const [newRate, setNewRate] = useState("")
  const [selectedFormat, setSelectedFormat] = useState<OpportunityFormat | null>(null)

  const { data: rateCards, isLoading } = useQuery({
    queryKey: ["host-rate-cards", profile?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("host_rate_card")
        .select("*")
        .eq("host_id", profile?.id)

      if (error) throw error
      return data as RateCard[]
    },
    enabled: !!profile?.id && !!session?.access_token
  })

  const addRateCard = useMutation({
    mutationFn: async () => {
      if (!selectedFormat || !newRate || !profile?.id) {
        throw new Error("Missing required fields")
      }

      // Check if a rate card already exists for this format
      const existingFormat = rateCards?.find(card => card.format === selectedFormat)
      if (existingFormat) {
        throw new Error("A rate card already exists for this format")
      }

      const { error } = await supabase
        .from("host_rate_card")
        .insert({
          host_id: profile?.id,
          format: selectedFormat,
          rate: parseFloat(newRate)
        })

      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["host-rate-cards"] })
      toast({
        title: "Success",
        description: "Rate card added successfully",
      })
      setNewRate("")
      setSelectedFormat(null)
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      })
    }
  })

  const deleteRateCard = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from("host_rate_card")
        .delete()
        .eq("id", id)

      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["host-rate-cards"] })
      toast({
        title: "Success",
        description: "Rate card deleted successfully",
      })
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      })
    }
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!selectedFormat || !newRate) {
      toast({
        title: "Error",
        description: "Please fill in all fields",
        variant: "destructive",
      })
      return
    }
    addRateCard.mutate()
  }

  if (isLoading) {
    return <div>Loading rate cards...</div>
  }

  const formats: OpportunityFormat[] = ["In-Office", "Virtual", "Hybrid", "Kiosk", "Outdoor"]
  const availableFormats = formats.filter(
    format => !rateCards?.some(card => card.format === format)
  )

  return (
    <div className="space-y-6">
    <div className="flex items-center justify-between">
      <h1 className="text-3xl font-bold">Rate Cards</h1>
    </div>
    <Card>
      <CardContent className="space-y-6 pt-6">
        {availableFormats.length === 0 ? (
          <Alert>
            <AlertDescription>
              All rate cards are present. Delete an existing rate card to add a new one.
            </AlertDescription>
          </Alert>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="format">Event Format</Label>
              <Select value={selectedFormat || ""} onValueChange={(value: OpportunityFormat) => setSelectedFormat(value)}>
                <SelectTrigger>
                  <SelectValue placeholder="Select format" />
                </SelectTrigger>
                <SelectContent>
                  {availableFormats.map(format => (
                    <SelectItem key={format} value={format}>{format}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="rate">Rate (per hour)</Label>
              <Input
                id="rate"
                type="number"
                value={newRate}
                onChange={(e) => setNewRate(e.target.value)}
                placeholder="Enter rate"
                min="0"
                step="0.01"
              />
            </div>

            <Button type="submit">Add Rate Card</Button>
          </form>
        )}

        <div className="space-y-4">
          <h4 className="font-medium">Current Rates</h4>
          <div className="grid gap-4">
            {rateCards?.map((card) => (
              <div
                key={card.id}
                className="flex items-center justify-between p-4 border rounded-lg"
              >
                <div>
                  <p className="font-medium">{card.format}</p>
                  <p className="text-sm text-gray-500">${card.rate}/hour</p>
                </div>
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={() => deleteRateCard.mutate(card.id)}
                >
                  Delete
                </Button>
              </div>
            ))}
            {rateCards?.length === 0 && (
              <p className="text-sm text-gray-500">No rate cards added yet.</p>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
    </div>
  )
}